import { queryKeys } from '@query/keys'
import { useQuery } from '@tanstack/react-query'

import { useAccountReadContext } from '@context/AccountContextContext'
import { useSessionLanguage } from '@context/SessionSettingsContext'

import { PageQuery, useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'
import { prefixFilter } from '@utils/filter.utils'

import { TransactionDto } from '@model/TransactionDto'
import { WalletTypeEnum } from '@model/WalletDto'

const RECENT_TRANSACTIONS_LIMIT = 5
const EMPTY_TRANSACTIONS: TransactionDto[] = []

export type WalletsRoom = 'trader' | 'ib' | 'paymentAgent'

interface UseRecentWalletTransactionsProps {
  room: WalletsRoom
  walletType: WalletTypeEnum
  walletId?: string
}

interface UseRecentWalletTransactionsResult {
  transactions: TransactionDto[]
  isLoading: boolean
  isError: boolean
}

const getRecentTransactionsQueryKey = (room: WalletsRoom, query: PageQuery, locale: string) => {
  const queryIdentity = { scope: 'wallets-recent', ...query }

  switch (room) {
    case 'ib':
      return queryKeys.ib.transactionHistory(queryIdentity, locale)
    case 'paymentAgent':
      return queryKeys.paymentAgent.transactionHistory(queryIdentity, locale)
    case 'trader':
      return queryKeys.transactionHistory(queryIdentity, locale)
  }
}

export const useRecentWalletTransactions = ({
  room,
  walletType,
  walletId,
}: UseRecentWalletTransactionsProps): UseRecentWalletTransactionsResult => {
  const { account } = useAccountReadContext()
  const locale = useSessionLanguage()
  const apiClient = useApiClient(ClientApiClient)

  const clientWalletType = account?.id
    ? prefixFilter({ prefix: account.id, value: walletType })
    : undefined

  const query: PageQuery = {
    pageIndex: 1,
    pageSize: RECENT_TRANSACTIONS_LIMIT,
    sort: 'CreatedDate',
    sortOrder: 'DESC',
    caller: 'ca',
    languageId: locale,
    ca_search_WalletId: walletId,
    ca_search_ClientWalletType: clientWalletType,
    ...(room === 'paymentAgent' && { ca_search_Client: account?.id }),
  }

  const transactionsQuery = useQuery({
    queryKey: getRecentTransactionsQueryKey(room, query, locale),
    queryFn: () => apiClient.getTransactions(query),
    select: ({ items }) =>
      [...items]
        .sort((left, right) => Date.parse(right.createdDate) - Date.parse(left.createdDate))
        .slice(0, RECENT_TRANSACTIONS_LIMIT),
    enabled: !!account?.id && !!walletId,
    refetchOnMount: 'always',
  })

  return {
    transactions: transactionsQuery.data ?? EMPTY_TRANSACTIONS,
    isLoading: transactionsQuery.isLoading,
    isError: transactionsQuery.isError && transactionsQuery.data === undefined,
  }
}
